import { Accordion, Link, ShadowContainer, Text } from "basis";
import { useEventTracking } from "react-event-tracker";

export const DirectDebitFAQ = () => {
  const { trackEvent } = useEventTracking();
  return (
    <ShadowContainer
      shadowSize="none"
      shadowSize-md="tiny"
      shadowContrast="medium"
      padding="4"
    >
      <Text textStyle="heading5" margin="0 0 4 0">
        Frequently asked questions
      </Text>
      <Accordion
        onItemToggle={({ isOpen, itemHeaderChildren }) => {
          if (isOpen)
            trackEvent({
              event: {
                category: "accordion",
                action: "in-page-navigation",
                location: "Frequently asked questions",
                label: itemHeaderChildren,
              },
            });
        }}
      >
        <Accordion.Item>
          <Accordion.Item.Header>
            How to set up a direct debit?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              You will need to let us know your bank details so we can arrange
              for funds to be debited from your nominated account and credited
              to your credit card account.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            Is direct debit available in New Zealand?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              Direct debit is not currently available for New Zealand products
              (GEM Visa & Gem CreditLine).
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            How do I cancel my direct debit?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              To cancel a direct debit on your account, fill in the{" "}
              <Link
                href="https://latitudefs.zendesk.com/hc/en-au/articles/360007246318-Cancel-or-suspend-a-direct-debit"
                newTab
              >
                request form
              </Link>
              .
            </Text>
            <Text margin="4 0 0 0">
              If your request is received before the due date of your minimum
              monthly payment, then allow at least 4 business days for the
              direct debit to be cancelled.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            Is direct debit available for personal loan customers?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              Direct debit is not currently available for personal loan
              accounts.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            Details of account to be debited
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              The details should appear as they do on your nominated account
              statement. We can only accept bank account details that are in the
              sole name of the account holder or from a joint bank account which
              includes the name of the account holder.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>Joint account holders</Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              We can only accept bank account details that are in the sole name
              of the account holder or from a joint bank account which includes
              the name of the account holder and the authority is{" "}
              <em>Anyone to Sign</em>.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            Minimum monthly payment + any overdue and overlimit amount
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              Your minimum monthly payment is the minimum amount you must pay to
              your credit card account every month, plus any overdue and
              overlimit amount.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            What date will the direct debit come out?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              Funds from your direct debit will come out on your statement due
              date which you can see on your account details just above the{" "}
              <em>Make a payment</em> button.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            When will my direct debit commence?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              When you setup a new direct debit it will start on the due date of
              your next statement. Please ensure that you pay your current
              statement via the Latitude mobile app, bpay or your post office.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            What happens if I don't have enough funds in my account?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              Your direct debit payment will be dishonoured if you don't have
              enough funds in your nominated account. Please ensure you have
              available funds in your account when your direct debit is due.
            </Text>
            <Text margin="4 0 0 0">
              <strong>Note:</strong> You may incur a late fee and a payment
              dishonour fee if your direct debit payment is dishonoured.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            Why was my direct debit request not approved?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              Your request to set up a direct debit payment may not have been
              approved if you've supplied an incorrect BSB or account number for
              your nominated bank account. Check your BSB and account number is
              correct when completing your request.
            </Text>
            <Text margin="4 0 0 0">
              You cannot set up a direct debit if your account is overdue. If
              you are overdue, please pay any outstanding amount using "one time
              payment" and then set up your direct debit.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            Can I make a direct debit payment more than once a month?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              No. Scheduled or recurring direct debit is only available for
              payments once a month. However, you can make additional payments
              using the Latitude mobile app or the Latitude Service Centre.
            </Text>
            <Text margin="4 0 0 0">
              The scheduled direct debit will be withdrawn from your nominated
              bank account regardless of whether you have made additional
              payments prior to the statement due date, except where the
              scheduled direct debit would put your Latitude account into
              credit, in which case a lesser amount will be withdrawn as
              required to bring the account balance to zero.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            How do I change my existing direct debit?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              You can change your direct debit details by submitting a new
              direct debit request. This will replace any existing details.
              Please note that the updated direct debit details will not take
              effect until the due date of your next statement. To avoid missing
              payment of the current statement, please make sure you have funds
              available in the bank account used by the existing direct debit.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            I don't have a balance to pay this month. Will anything be debited
            from my bank account?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              No. If you don't have a balance to pay on your current statement
              then no funds will be debited from your bank account.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
        <Accordion.Item>
          <Accordion.Item.Header>
            Does the direct debit apply to all my Latitude products?
          </Accordion.Item.Header>
          <Accordion.Item.Content>
            <Text>
              No, you will need to set up a direct debit for each Latitude
              product. At the moment, this facility to set up a direct debit is
              not available for personal loans.
            </Text>
          </Accordion.Item.Content>
        </Accordion.Item>
      </Accordion>
    </ShadowContainer>
  );
};
